import dayjs from 'dayjs'
import { ALL_DATES, resolveFilterDefault, toFilterValue } from './cbDateSettings'
import { datesDefaultToAllDates } from '@components/reports/reportFeatureConfig'
import { CURRENT_USER_MACRO, idOf } from './reportFilterWire'

/**
 * Seed a report's filter values from its definition.
 *
 * Extracted from ReportView so ANY surface that runs a report (the report page,
 * the Queue Report modal) seeds filters identically. This is not boilerplate —
 * it carries four separate fixes (CC-49840 all-dates WIP reports, Bug #5
 * SIMPLEDATE anchoring, CC-49925 BOOL, and the unresolved-macro crash), so a
 * second hand-rolled copy would silently run reports with the wrong filters.
 */

export const boolDefaultValue = (col) => {
  const raw = String(col.filterDefault ?? '').trim().toLowerCase()
  return raw === '1' || raw === 'true' || raw === 'yes' ? '1' : '0'
}

/**
 * Map each filter column's `filterDefault` to its initial value.
 * DATE columns get their CB DateSettings JSON / pipe range / macro resolved to a
 * literal "YYYY-MM-DD|YYYY-MM-DD" or "$$cb_all" so the user sees real dates in
 * the inputs and the server gets a value it can run with (rather than crashing
 * on the unresolved macro JSON).
 */
export const buildInitialFilters = (definition) => {
  const out = {}
  // CC-49840: WIP-family reports open at All Dates so the running total isn't
  // period-scoped by CB's own "This Month"/"This Year" default (approved
  // divergence from V1). Only the initial value — the filter stays usable and a
  // persisted/saved selection still wins. SIMPLEDATE anchors are untouched.
  const allDatesByDefault = datesDefaultToAllDates(definition)
  for (const col of definition?.columns || []) {
    if (!col.filterable) continue
    const t = col.filterTypeName
    if (t === 'DATE' && allDatesByDefault) {
      out[col.columnId] = ALL_DATES
    } else if (t === 'SIMPLEDATE') {
      // SIMPLEDATE is a required single "as of" anchor — CB's proc throws on an
      // empty value, so it must never start blank. Resolve the server default;
      // Bug #5: a SIMPLEDATE default like "Today" resolves to a RANGE, so take
      // the range start (fall back to its end). When there is NO server default
      // (or it resolves to nothing), anchor to today so a deep-linked / first-
      // open report RUNS instead of dead-ending on the required-date guard
      // (matches AP/AR as-of-date behavior). "All Dates" still passes through
      // when the server default says so.
      const resolved = col.filterDefault ? resolveFilterDefault(col.filterDefault) : null
      const today = dayjs().format('YYYY-MM-DD')
      out[col.columnId] = resolved
        ? (resolved.all ? ALL_DATES : (resolved.from || resolved.to || today))
        : today
    } else if (t === 'DATE' && col.filterDefault) {
      const resolved = resolveFilterDefault(col.filterDefault)
      out[col.columnId] = resolved ? toFilterValue(resolved) : ''
    } else if (t === 'BOOL') {
      // CC-49925: a BOOL is ALWAYS applied — there is no "Both"/omit state in CB
      // core (its checkbox always posts True/False). Seed to "1"/"0" from the
      // definition default. Supersedes the CC-49463 Yes/No/Both tri-state.
      out[col.columnId] = boolDefaultValue(col)
    } else if (col.filterDefault != null) {
      out[col.columnId] = col.filterDefault
    }
  }
  return out
}

/**
 * Spec 164: a persisted filter set that still holds the raw `$$cb_userid` macro on a
 * LOOKUP column takes that column's default instead.
 *
 * Every user who opened report 121 (and 226 / 281) before cb-api resolved the macro has
 * it saved in localStorage (`cb:report-filters:v2:*`), because the page persists its
 * opening values. Restored as-is, the panel would keep reading "Timekeeper: 1 selected"
 * with an empty lookup box. The default now carries "<id>:<UserName>" for the signed-in
 * user, so the panel names them and the wire sends their id. The wire makes the same
 * swap on its own (reportFilterWire.js), so this is about what the panel SAYS.
 *
 * Only that value on that column type is touched; every other stored choice survives.
 * Returns the same object when nothing applies.
 */
export const reconcileStoredCurrentUser = (stored, defaults, definition) => {
  if (!stored || typeof stored !== 'object') return stored
  let out = stored
  for (const col of definition?.columns || []) {
    if (!col?.filterable || col.filterTypeName !== 'LOOKUP') continue
    const value = stored[col.columnId]
    if (value == null) continue
    const holdsMacro = String(value).split('|').some((part) => idOf(part) === CURRENT_USER_MACRO)
    if (!holdsMacro) continue
    if (out === stored) out = { ...stored }
    out[col.columnId] = defaults?.[col.columnId] ?? ''
  }
  return out
}

/**
 * A finished job's own filter values, as a `filterValues` map (CC-53051).
 *
 * `savedParams` for a GENERIC job is `{ <columnId>: <wire value> }` — cb-api derives it
 * from the filter array the run was created with (CC-53048), so it is a record of what
 * ACTUALLY RAN rather than of what the user has since selected.
 *
 * ## Why this exists
 *
 * "Run again, same filters" did not use the job's filters. Reproduced live on
 * 2026-08-18 against the deployed host: clicking it on a run of report 190 filtered
 * `2019-02-23|2019-03-02` (52 rows) fired a fresh `POST /reports/jobs` carrying
 * `2020-02-23|2020-03-24` — the user's *persisted* filter set — which returned 12 rows.
 * The affordance ran a different report from the one it named, and on a billing or A/R
 * report that is a wrong-number path, not a labelling nit.
 *
 * ## Deliberately conservative
 *
 * Only keys that match a **filterable column on this definition** are taken. A generic
 * job's `savedParams` can carry keys that are no longer filterable (a report definition
 * can change between the run and the reopen), and feeding those into the panel would put
 * values into controls that do not exist — which the wire then either ignores or, worse,
 * binds to nothing. Anything unmatched is dropped and the caller falls back for it.
 *
 * Values are passed through UNCHANGED. They are already in wire form (`"YYYY-MM-DD|YYYY-MM-DD"`,
 * `"$$cb_all"`, `'1'`/`'0'` for a BOOL), and re-deriving them here is how the panel would
 * come to disagree with the run it claims to reproduce.
 *
 * @returns {object|null} null when nothing usable is present — the caller must then keep
 *          its existing behaviour rather than run with an empty filter set, which CB
 *          would happily execute UNSCOPED.
 */
export const savedParamsToFilterValues = (definition, savedParams) => {
  if (!definition || !savedParams || typeof savedParams !== 'object' || Array.isArray(savedParams)) return null
  const filterable = new Set(
    (definition.columns || []).filter((c) => c.filterable).map((c) => String(c.columnId))
  )
  const out = {}
  for (const [key, value] of Object.entries(savedParams)) {
    if (!filterable.has(String(key))) continue
    out[key] = value ?? ''
  }
  return Object.keys(out).length ? out : null
}

export default buildInitialFilters
